// Driver home screen — thin adapter around the driver branch that previously
// lived inline in app/dashboard/home.tsx. The JSX is intentionally identical
// to the legacy inline render so the new feature flag is a pure render-path
// switch (flag OFF = old inline branch, flag ON = this component). Rollback
// is simply flipping EXPO_PUBLIC_DRIVER_HOME_SCREEN_ENABLED back to off and
// rebuilding.

import { Platform, View, StyleSheet, Linking } from 'react-native';
import { Portal, Dialog, Text, Button, Surface } from 'react-native-paper';
import type { MD3Theme } from 'react-native-paper';

import { Sidebar, SIDEBAR_WIDTH } from '../../../components/Sidebar';
import { EmploymentModal } from '../../../components/EmploymentModal';
import { VehicleModal } from '../../../components/VehicleModal';
import { SessionModal } from '../../../components/SessionModal';
import { PendingWarningModal } from '../../../components/PendingWarningModal';
import { OdometerVelocityModal } from '../../../components/OdometerVelocityModal';
import { OdometerCorrectionModal } from '../../../components/OdometerCorrectionModal';
import { DashboardContent } from '../../../components/DashboardContent';
import { DriverProfileRecoveryState } from '../../../components/DriverProfileRecoveryState';
import { shouldShowEmploymentModal } from '../../../utils/driverEmploymentGate';
import { LoadingScreen } from '../../../components/LoadingScreen';
import type { DrivingLimitStatus, DrivingWeekLimits, OdometerVelocityWarning } from '../../../api/apiService';
import type { LocationRegion, OdometerFormData, Vehicle } from '../../../lib/types';

type ProfileValue = 'Owner' | 'Driver';

export interface DriverHomeScreenProps {
  // employment / vehicle gating
  hasActiveEmployment: boolean | null;
  /**
   * WS7 (#207): the driver funds their own (companyless) license, so the
   * employment they declared is informative — it must not gate the app.
   * Tri-state (boolean | null): null = entitlement not yet resolved
   * (hydration) — the modal must stay closed while unknown.
   */
  hasSelfPayLicense?: boolean | null;
  /** #207: sair do ecrã de ativação pendente pagando a própria licença. */
  onSelfPay?: () => void;
  /** Self-pay subscription exists but needs payment recovery in the Portal. */
  paymentIssue?: boolean;
  onFixPayment?: () => void | Promise<void>;
  hasVehicles: boolean | null;
  isLoading: boolean;
  employments: any[];
  hasDriver: boolean;
  hasOwner: boolean;
  effectiveProfile: ProfileValue;
  updateProfile: (profile: ProfileValue) => void;
  hasInitializedCache: boolean;
  initializationTimeout: boolean;
  isRedirectingToOnboarding: boolean;
  reloadApp: () => Promise<void> | void;

  // layout
  width: number;
  isLargeScreen: boolean;
  desktopSidebarVisible: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  theme: MD3Theme;

  // user / company
  handleLogout: () => Promise<void> | void;
  companyForm: any;
  handleAddCompany: (...args: any[]) => any;
  modalVisible: boolean;
  setModalVisible: (v: boolean) => void;
  fromPendingWarning: boolean;
  setFromPendingWarning: (v: boolean) => void;

  // vehicle modal
  vehicleModalVisible: boolean;
  setVehicleModalVisible: (v: boolean) => void;
  vehicleForm: any;
  handleAddVehicle: (...args: any[]) => any;
  localRegNumber: string;
  setLocalRegNumber: (v: string) => void;
  formatRegistrationNumber: (v: string) => string;

  // driving state
  location: LocationRegion | null;
  trackingDegraded: boolean;
  isSessionActive: boolean;
  isDriving: boolean;
  drivingTimeToday: string;
  drivingLimit?: DrivingLimitStatus | null;
  drivingWeekLimits?: DrivingWeekLimits | null;
  stateSince?: string | null;
  actionInProgress: boolean;
  onStartSession: () => Promise<void> | void;
  onEndSession: () => Promise<void> | void;
  onStartDriving: () => Promise<void> | void;
  onStopDriving: () => Promise<void> | void;
  isStartingSession: boolean;
  isEndingSession: boolean;
  isStartingDriving: boolean;
  isStoppingDriving: boolean;
  isVelocityConfirming: boolean;
  showMovementAlert: () => void;
  showStationaryAlert: () => void;

  // session modals
  startSessionModalVisible: boolean;
  setStartSessionModalVisible: (v: boolean) => void;
  endSessionModalVisible: boolean;
  setEndSessionModalVisible: (v: boolean) => void;
  startOdometerForm: any;
  endOdometerForm: any;
  handleStartSessionSubmit: (data: OdometerFormData) => Promise<any> | any;
  handleStartSessionWithProvidedLocationWrapper: (data: OdometerFormData, locationWkt: string) => Promise<boolean>;
  handleEndSessionSubmit: (data: OdometerFormData) => Promise<any> | any;
  handleEndSessionSubmitWithProvidedLocation: (data: OdometerFormData, locationWkt: string) => Promise<boolean>;
  selectedVehicle: number | null;
  setSelectedVehicle: (id: number | null) => void;
  vehicles: Vehicle[];
  latestOdometer: number | null;
  retryLocationPermission: () => Promise<void> | void;

  // odometer warning + correction
  velocityWarningVisible: boolean;
  setVelocityWarningVisible: (v: boolean) => void;
  velocityWarningData: OdometerVelocityWarning | null;
  setVelocityWarningData: (v: OdometerVelocityWarning | null) => void;
  velocityWarningContext: 'start' | 'end' | null;
  setVelocityWarningContext: (v: 'start' | 'end' | null) => void;
  pendingSessionData: OdometerFormData | null;
  setPendingSessionData: (v: OdometerFormData | null) => void;
  setPendingLocationWkt: (v: string | null) => void;
  handleVelocityWarningConfirm: () => Promise<void> | void;
  correctionModalVisible: boolean;
  setCorrectionModalVisible: (v: boolean) => void;
  correctionContext: 'start' | 'end' | null;
  setCorrectionContext: (v: 'start' | 'end' | null) => void;
  previousSessionOut: { id: number; odometer: number; timestamp: string } | null;
  setPreviousSessionOut: (v: { id: number; odometer: number; timestamp: string } | null) => void;
  newOdometerValue: number | null;
  setNewOdometerValue: (v: number | null) => void;
  handleOdometerCorrection: (correctedOdometer: number, observations: string) => Promise<void>;
  isCorrectingOdometer: boolean;

  // alerts + dialogs
  alertModalVisible: boolean;
  alertMessage: string;
  alertAction: (() => void | Promise<void>) | null;
  dismissAlertFlow: () => void;
  locationPermissionError: boolean;
  locationSettingsError: boolean;
  setLocationSettingsError: (v: boolean) => void;
  purchaseSuccessModal: boolean;
  setPurchaseSuccessModal: (v: boolean) => void;
  purchaseData: { licenses: number; productId: string; isNew: boolean } | null;

  // i18n
  t: (key: string, fallback?: string) => string;
}

export function DriverHomeScreen(props: DriverHomeScreenProps) {
  const {
    hasActiveEmployment,
    hasSelfPayLicense = false, // degrade null (unknown) to false for display/vehicle ops
    onSelfPay,
    paymentIssue = false,
    onFixPayment,
    hasVehicles,
    isLoading,
    employments,
    hasDriver,
    hasOwner,
    effectiveProfile,
    updateProfile,
    hasInitializedCache,
    initializationTimeout,
    isRedirectingToOnboarding,
    reloadApp,
    width,
    isLargeScreen,
    desktopSidebarVisible,
    sidebarOpen,
    setSidebarOpen,
    theme,
    handleLogout,
    companyForm,
    handleAddCompany,
    modalVisible,
    setModalVisible,
    fromPendingWarning,
    setFromPendingWarning,
    vehicleModalVisible,
    setVehicleModalVisible,
    vehicleForm,
    handleAddVehicle,
    localRegNumber,
    setLocalRegNumber,
    formatRegistrationNumber,
    location,
    trackingDegraded,
    isSessionActive,
    isDriving,
    drivingTimeToday,
    drivingLimit,
    drivingWeekLimits,
    stateSince,
    actionInProgress,
    onStartSession,
    onEndSession,
    onStartDriving,
    onStopDriving,
    isStartingSession,
    isEndingSession,
    isStartingDriving,
    isStoppingDriving,
    isVelocityConfirming,
    showMovementAlert,
    showStationaryAlert,
    startSessionModalVisible,
    setStartSessionModalVisible,
    endSessionModalVisible,
    setEndSessionModalVisible,
    startOdometerForm,
    endOdometerForm,
    handleStartSessionSubmit,
    handleStartSessionWithProvidedLocationWrapper,
    handleEndSessionSubmit,
    handleEndSessionSubmitWithProvidedLocation,
    selectedVehicle,
    setSelectedVehicle,
    vehicles,
    latestOdometer,
    retryLocationPermission,
    velocityWarningVisible,
    setVelocityWarningVisible,
    velocityWarningData,
    setVelocityWarningData,
    velocityWarningContext,
    setVelocityWarningContext,
    pendingSessionData,
    setPendingSessionData,
    setPendingLocationWkt,
    handleVelocityWarningConfirm,
    correctionModalVisible,
    setCorrectionModalVisible,
    correctionContext,
    setCorrectionContext,
    previousSessionOut,
    setPreviousSessionOut,
    newOdometerValue,
    setNewOdometerValue,
    handleOdometerCorrection,
    isCorrectingOdometer,
    alertModalVisible,
    alertMessage,
    alertAction,
    dismissAlertFlow,
    locationPermissionError,
    locationSettingsError,
    setLocationSettingsError,
    purchaseSuccessModal,
    setPurchaseSuccessModal,
    purchaseData,
    t,
  } = props;

  // Show company modal if user has no employment.
  // WS7 (#207): a self-pay driver is entitled by their OWN license, so the
  // declared employment is informative — they must NEVER see the
  // "Adicionar Informações da Empresa" modal (that is the company-funded
  // onboarding path). Without this guard a freshly hydrated self-pay driver
  // (users/me -> employments: []) would render this modal over the dashboard
  // and `driver-dashboard` would never become visible (jobs 15920979476/79).
  if (shouldShowEmploymentModal({ hasActiveEmployment, employments, hasSelfPayLicense }) && hasDriver) {
    return (
      <View style={styles.container}>
        <EmploymentModal
          visible={true}
          onDismiss={() => {}}
          fromPendingWarning={false}
          onBack={() => {}}
          form={companyForm}
          onSubmit={handleAddCompany}
          onLogout={handleLogout}
        />
      </View>
    );
  }

  // The employment gate belongs to the company-funded path: a driver with a
  // license of their own operates while the declared employment stays
  // informative (WS7/#207 — it is what puts them in the owner's report).
  const canOperate = hasActiveEmployment === true || hasSelfPayLicense;

  // Show PendingWarningModal only after we have employment data and there are
  // pending employments — never to a driver who paid for their own license.
  if (!canOperate && employments.length > 0) {
    return (
      <View style={styles.container}>
        <PendingWarningModal
          visible={true}
          employments={employments}
          onSelfPay={onSelfPay}
          paymentIssue={paymentIssue}
          onFixPayment={onFixPayment}
          onAddAnotherCompany={undefined}
          onSwitchProfile={hasOwner ? (newProfile: ProfileValue) => { updateProfile(newProfile); } : undefined}
          onLogout={handleLogout}
        />
      </View>
    );
  }

  // Show vehicle modal if the driver may operate but has no vehicles
  if (canOperate && hasVehicles === false) {
    if (isRedirectingToOnboarding) {
      return null;
    }

    if (!hasInitializedCache) {
      return (
        <View style={styles.container}>
          <LoadingScreen />
        </View>
      );
    }

    // A server-complete driver without a vehicle is already on the dashboard.
    // Keep the extracted/feature-flagged path aligned with the legacy path:
    // show explicit recovery instead of claiming to redirect forever.
    return (
      <>
        <DriverProfileRecoveryState
          desktopSidebarVisible={desktopSidebarVisible}
          width={width}
          handleLogout={handleLogout}
          hasOwner={hasOwner}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner ? (newProfile: ProfileValue) => { updateProfile(newProfile); } : undefined}
          onOpenVehicleSetup={() => setVehicleModalVisible(true)}
        />
        <VehicleModal
          visible={vehicleModalVisible}
          onDismiss={() => setVehicleModalVisible(false)}
          form={vehicleForm}
          onSubmit={handleAddVehicle}
          localRegNumber={localRegNumber}
          setLocalRegNumber={setLocalRegNumber}
          formatRegistrationNumber={formatRegistrationNumber}
        />
      </>
    );
  }

  // Loading vehicles state
  if (canOperate && (hasVehicles === null || isLoading)) {
    if (initializationTimeout) {
      return (
        <View style={[styles.container, { padding: 24, justifyContent: 'center', alignItems: 'center' }]}>
          <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16, color: theme.colors.error }}>
            {t('errors.errorLoadingData')}
          </Text>
          <Text style={{ textAlign: 'center', marginBottom: 24 }}>
            {t('errors.errorLoadingDataMessage')}
          </Text>
          <Button mode="contained" onPress={() => { void reloadApp(); }}>
            {t('errors.reloadPage')}
          </Button>
        </View>
      );
    }

    return (
      <LoadingScreen message={!hasInitializedCache ? t('loading.checkingData') : t('loading.loadingVehicles')} />
    );
  }

  // Main driver dashboard
  if (canOperate && hasVehicles === true) {
    return (
      <View testID="driver-dashboard" style={styles.container}>
        {desktopSidebarVisible && (
          <Sidebar
            isOpen={true}
            onClose={() => {}}
            screenWidth={width}
            onLogout={handleLogout}
            isOwner={hasOwner}
            isDriver={true}
            selectedProfile={effectiveProfile}
            onSwitchProfile={hasOwner ? (newProfile: ProfileValue) => { updateProfile(newProfile); } : undefined}
          />
        )}

        <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
          <DashboardContent
            location={location}
            trackingDegraded={trackingDegraded}
            isSessionActive={isSessionActive}
            isDriving={isDriving}
            drivingTimeToday={drivingTimeToday}
            drivingLimit={drivingLimit}
            drivingWeekLimits={drivingWeekLimits}
            stateSince={stateSince}
            actionInProgress={actionInProgress}
            onOpenSidebar={() => setSidebarOpen(true)}
            onStartSession={onStartSession}
            onEndSession={onEndSession}
            onStartDriving={onStartDriving}
            onStopDriving={onStopDriving}
            isStartingSession={isStartingSession}
            isEndingSession={isEndingSession}
            isStartingDriving={isStartingDriving}
            isStoppingDriving={isStoppingDriving}
            canEndSession={isDriving === false}
            onMovementAlert={showMovementAlert}
            onStationaryAlert={showStationaryAlert}
            // HOTFIX: phone verification disabled until migration is stable in production
            phoneVerified={true}
            phoneNumber={undefined}
            onVerifyPhonePress={undefined}
          />
        </Surface>

        {!isLargeScreen && (
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            screenWidth={width}
            onLogout={handleLogout}
            isOwner={hasOwner}
            isDriver={true}
            selectedProfile={effectiveProfile}
            onSwitchProfile={hasOwner ? (newProfile: ProfileValue) => { updateProfile(newProfile); } : undefined}
          />
        )}

        <EmploymentModal
          visible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          fromPendingWarning={fromPendingWarning}
          onBack={() => {
            setModalVisible(false);
            setFromPendingWarning(false);
          }}
          form={companyForm}
          onSubmit={handleAddCompany}
          onLogout={handleLogout}
        />
        <VehicleModal
          visible={vehicleModalVisible}
          onDismiss={() => setVehicleModalVisible(false)}
          form={vehicleForm}
          onSubmit={handleAddVehicle}
          localRegNumber={localRegNumber}
          setLocalRegNumber={setLocalRegNumber}
          formatRegistrationNumber={formatRegistrationNumber}
        />
        <SessionModal
          visible={startSessionModalVisible}
          onDismiss={() => setStartSessionModalVisible(false)}
          form={startOdometerForm}
          onSubmit={handleStartSessionSubmit}
          onSubmitWithLocation={handleStartSessionWithProvidedLocationWrapper}
          selectedVehicle={selectedVehicle}
          vehicles={vehicles}
          onSelectVehicle={setSelectedVehicle}
          allowVehicleSelection={true}
          title="startSession"
          buttonText="startSession"
          isLoading={isStartingSession}
          latestOdometer={latestOdometer}
          retryLocationPermission={retryLocationPermission}
        />
        <SessionModal
          visible={endSessionModalVisible}
          onDismiss={() => setEndSessionModalVisible(false)}
          form={endOdometerForm}
          onSubmit={handleEndSessionSubmit}
          onSubmitWithLocation={handleEndSessionSubmitWithProvidedLocation}
          selectedVehicle={selectedVehicle}
          vehicles={vehicles}
          onSelectVehicle={setSelectedVehicle}
          allowVehicleSelection={false}
          title="endSession"
          buttonText="endSession"
          isLoading={isEndingSession}
          latestOdometer={latestOdometer}
          retryLocationPermission={retryLocationPermission}
        />
        {velocityWarningData && (
          <OdometerVelocityModal
            visible={velocityWarningVisible}
            onDismiss={() => {
              setVelocityWarningVisible(false);
              setVelocityWarningData(null);
              setPendingSessionData(null);
              setPendingLocationWkt(null);
              setVelocityWarningContext(null);
              if (velocityWarningContext === 'start') {
                setStartSessionModalVisible(true);
              }
              if (velocityWarningContext === 'end') {
                setEndSessionModalVisible(true);
              }
            }}
            onConfirm={handleVelocityWarningConfirm}
            meanVelocityKmh={velocityWarningData.mean_velocity_kmh}
            previousOdometer={velocityWarningData.previous_odometer}
            newOdometer={pendingSessionData?.odometer || 0}
            timeDifferenceHours={velocityWarningData.time_difference_hours}
            isLoading={velocityWarningContext === 'end' ? (isEndingSession || isVelocityConfirming) : isStartingSession}
          />
        )}
        {previousSessionOut && newOdometerValue !== null && (
          <OdometerCorrectionModal
            visible={correctionModalVisible}
            onDismiss={() => {
              const isEnd = correctionContext === 'end';
              setCorrectionModalVisible(false);
              setCorrectionContext(null);
              setPreviousSessionOut(null);
              setNewOdometerValue(null);
              setPendingSessionData(null);
              if (isEnd) {
                setEndSessionModalVisible(true);
              } else {
                setStartSessionModalVisible(true);
              }
            }}
            onConfirm={handleOdometerCorrection}
            previousSessionOut={previousSessionOut}
            newOdometer={newOdometerValue}
            isLoading={isCorrectingOdometer}
          />
        )}
        <PendingWarningModal
          visible={!canOperate && employments.length > 0}
          employments={employments}
          onSelfPay={onSelfPay}
          onAddAnotherCompany={() => {
            setModalVisible(true);
            setFromPendingWarning(true);
          }}
          onSwitchProfile={hasOwner ? (newProfile: ProfileValue) => { updateProfile(newProfile); } : undefined}
          onLogout={handleLogout}
        />
        <Portal>
          <Dialog visible={alertModalVisible} onDismiss={dismissAlertFlow} style={Platform.OS === 'web' ? {
            maxWidth: 500,
            width: '90%',
            alignSelf: 'center',
          } : {}}>
            <Dialog.Title>{t('alerts.alert')}</Dialog.Title>
            <Dialog.Content>
              <Text variant="bodyMedium">{alertMessage}</Text>
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={dismissAlertFlow}>{t('cancel')}</Button>
              <Button onPress={() => { void alertAction?.(); }}>{t('alerts.confirm')}</Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>
        <Portal>
          <Dialog visible={locationPermissionError} dismissable={false} style={Platform.OS === 'web' ? {
            maxWidth: 600,
            width: '90%',
            alignSelf: 'center',
          } : {}}>
            <Dialog.Title>{t('alerts.locationPermissionRequired')}</Dialog.Title>
            <Dialog.Content>
              <Text variant="bodyMedium">
                {t('alerts.locationPermissionRequiredMessage')}
              </Text>
              <Text variant="bodyMedium" style={{ marginTop: 12, color: theme.colors.error }}>
                {t('alerts.locationPermissionRetryMessage')}
              </Text>
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={retryLocationPermission}>{t('alerts.tryAgain')}</Button>
              <Button onPress={() => { void reloadApp(); }}>{t('alerts.refreshPage')}</Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>

        {/* Location settings (GPS) error dialog */}
        <Portal>
          <Dialog
            visible={locationSettingsError}
            onDismiss={() => setLocationSettingsError(false)}
            dismissable={!!(Platform.OS === 'web')}
            style={Platform.OS === 'web' ? { maxWidth: 600, width: '90%', alignSelf: 'center' } : {}}
          >
            <Dialog.Title>{t('alerts.locationSettingsInvalid')}</Dialog.Title>
            <Dialog.Content>
              <Text variant="bodyMedium">
                {t('alerts.locationSettingsInvalidMessage')}
              </Text>
            </Dialog.Content>
            <Dialog.Actions>
              {Platform.OS !== 'web' && (
                <Button onPress={() => Linking.openSettings()}>
                  {t('alerts.openDeviceSettings')}
                </Button>
              )}
              <Button onPress={() => setLocationSettingsError(false)}>
                {t('alerts.tryAgain')}
              </Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>

        {/* Purchase Success Modal */}
        <Portal>
          <Dialog
            visible={purchaseSuccessModal}
            onDismiss={() => setPurchaseSuccessModal(false)}
            style={Platform.OS === 'web' ? {
              maxWidth: 500,
              width: '90%',
              alignSelf: 'center',
            } : {}}
          >
            <Dialog.Title style={{ color: theme.colors.primary }}>
              {t('purchase.success.title')}
            </Dialog.Title>
            <Dialog.Content>
              <Text variant="bodyLarge" style={{ marginBottom: 16 }}>
                {purchaseData?.isNew
                  ? t('purchase.success.messageNew')
                  : t('purchase.success.messageUpdated')
                }
              </Text>
              <View style={{
                backgroundColor: theme.colors.primaryContainer,
                padding: 16,
                borderRadius: 8,
                marginTop: 8,
              }}>
                <Text variant="titleMedium" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  {t('purchase.success.licenses')}
                </Text>
                <Text variant="headlineMedium" style={{
                  color: theme.colors.primary,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  marginTop: 4,
                }}>
                  {purchaseData?.licenses} {t('purchase.success.gpsLicenses')}
                </Text>
              </View>
              <Text variant="bodyMedium" style={{ marginTop: 16, opacity: 0.7 }}>
                {t('purchase.success.ready')}
              </Text>
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={() => setPurchaseSuccessModal(false)}>
                {t('purchase.success.close')}
              </Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <LoadingScreen />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
});
